import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import BlogContent from '../components/blog/BlogContent';
import { blogPosts } from '../data/content';

const BlogArticle = () => {
  const { slug } = useParams();
  const article = blogPosts.find(post => post.id === slug);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-semibold mb-4">Article Not Found</h1>
        <Link to="/blog">
          <Button>Back to Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-foreground">Blog</Link>
            <span>/</span>
            <span className="text-foreground line-clamp-1">{article.title}</span>
          </div>
        </div>
      </div>

      <article className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Blog</span>
          </Link>

          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <motion.div className="mb-12 space-y-6" {...fadeInUp}>
              <Badge variant="secondary">{article.category}</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                {article.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>{article.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>{article.readTime}</span>
                </div>
              </div>
            </motion.div>

            {/* Article Content */}
            <motion.div 
              className="mb-12"
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              data-testid="blog-article-content"
            >
              <div 
                className="prose prose-lg max-w-none prose-headings:font-semibold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:mb-4 prose-p:leading-relaxed prose-ul:my-4 prose-li:mb-2"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </motion.div>

            {/* CTA Card */}
            <motion.div {...fadeInUp} transition={{ delay: 0.3 }}>
              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-semibold">Need Expert Advice?</h3>
                  <p className="opacity-90">
                    Our team is ready to help you with your FIBC manufacturing needs.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Link to="/contact">
                      <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                        Contact Us
                      </Button>
                    </Link>
                    <Link to="/products">
                      <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary">
                        View Products
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;
